import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  FileClock,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Plus,
  Search,
  Settings2,
  Upload,
  Users,
  Wrench,
} from "lucide-react";
import type { UserRole } from "../contract.js";
import { useListAppsQuery, useLogoutMutation, useMeQuery } from "../store/api.js";
import { loggedOut, userRefreshed } from "../store/auth.slice.js";
import { useAppDispatch, useAppSelector } from "../store/index.js";
import { Brand } from "./Brand.js";
import { PublishDialog } from "./PublishDialog.js";
import { UploadDock } from "./UploadDock.js";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const COLLAPSE_KEY = "ota.sidebar.collapsed";

const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Administrator",
  releaser: "Perilis",
  analyst: "Analis",
  viewer: "Peninjau",
};

interface NavEntry {
  to: string;
  label: string;
  icon: typeof Package;
  roles?: UserRole[];
  badge?: string;
  isAction?: boolean;
}

const PAGE_BREADCRUMBS: Record<string, { group: string; page: string }> = {
  "/": { group: "Aplikasi", page: "Ringkasan" },
  "/releases": { group: "Rilis", page: "Semua Rilis" },
  "/builds": { group: "Rilis", page: "Upload APK & IPA" },
  "/analytics": { group: "Penggunaan", page: "Analitik" },
  "/setup": { group: "Sistem", page: "Setup & Integrasi" },
  "/users": { group: "Sistem", page: "Pengguna" },
  "/audit": { group: "Sistem", page: "Log Audit" },
};

export function Layout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refreshToken } = useAppSelector((state) => state.auth);
  const { data: me } = useMeQuery();
  const { data: appsData } = useListAppsQuery();
  const [logout] = useLogoutMutation();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [mobileNav, setMobileNav] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [term, setTerm] = useState(searchParams.get("q") ?? "");

  useEffect(() => {
    if (!me || !user) return;
    const unchanged =
      me.username === user.username &&
      me.role === user.role &&
      me.mustChangePassword === user.mustChangePassword;
    if (unchanged) return;
    dispatch(userRefreshed(me));
  }, [me, user, dispatch]);

  const apps = appsData ?? [];
  const scopeApp = searchParams.get("app") ?? (apps[0]?.slug ?? "digibikes");
  const scopeChannel = searchParams.get("channel") ?? "";
  const currentApp = apps.find((a) => a.slug === scopeApp) ?? apps[0];
  const channels = currentApp?.channels ?? [];

  const breadcrumb = PAGE_BREADCRUMBS[location.pathname] ?? {
    group: currentApp?.name ?? "Konsol OTA",
    page: "Halaman",
  };

  useEffect(() => {
    document.title = `${breadcrumb.page} · YKKBI`;
  }, [breadcrumb]);

  const queryParam = searchParams.get("q") ?? "";
  useEffect(() => {
    setTerm(queryParam);
  }, [queryParam]);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname]);

  async function signOut() {
    try {
      if (refreshToken) await logout({ refreshToken }).unwrap();
    } catch {
      // Offline logout fallback
    }
    dispatch(loggedOut());
    navigate("/login", { replace: true });
  }

  function handleSelectApp(slug: string) {
    const next = new URLSearchParams(searchParams);
    next.set("app", slug);
    next.delete("channel"); // Reset channel to default of selected app
    setSearchParams(next, { replace: true });
  }

  function handleSelectChannel(channelName: string) {
    const next = new URLSearchParams(searchParams);
    if (channelName && channelName !== "__all") {
      next.set("channel", channelName);
    } else {
      next.delete("channel");
    }
    setSearchParams(next, { replace: true });
  }

  function submitSearch(event: React.FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (term.trim()) next.set("q", term.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }

  const role = user?.role;
  const canRelease = role === "admin" || role === "releaser";

  // Navigation items: only real database-backed features
  const navGroups: { title?: string; items: NavEntry[] }[] = [
    {
      items: [{ to: "/", label: "Ringkasan", icon: LayoutDashboard }],
    },
    {
      title: "RILIS",
      items: [
        { to: "/releases", label: "Semua Rilis", icon: Package },
        { to: "/builds", label: "Upload APK & IPA", icon: Upload },
      ],
    },
    {
      title: "PENGGUNAAN",
      items: [
        { to: "/analytics", label: "Analitik", icon: BarChart3 },
      ],
    },
    {
      title: "SISTEM",
      items: [
        { to: "/setup", label: "Setup & Integrasi", icon: Wrench },
        { to: "/users", label: "Pengguna", icon: Users, roles: ["admin"] },
        { to: "/audit", label: "Log Audit", icon: FileClock, roles: ["admin", "releaser", "analyst"] },
      ],
    },
  ];

  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || (role && item.roles.includes(role))),
    }))
    .filter((group) => group.items.length > 0);

  const initials = (user?.username ?? "AO").slice(0, 2).toUpperCase();

  const sidebarContent = (
    <div className="flex h-full flex-col justify-between bg-white text-[#101828]">
      <div className="flex flex-col">
        {/* Brand Lockup */}
        <div className={cn("flex items-center px-4 pt-4 pb-3", collapsed && "justify-center px-2")}>
          <Brand compact={collapsed} />
        </div>

        {/* Scope App Switcher */}
        {!collapsed ? (
          <div className="px-3 pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="w-full flex items-center justify-between rounded-lg border border-[#EAECF0] bg-[#F6F7F9] p-2 hover:bg-[#F0F2F5] transition-colors text-left focus:outline-none cursor-pointer"
                aria-label="Pilih aplikasi"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="size-7 rounded-lg bg-[#0F1B3D] text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {currentApp?.name?.charAt(0) ?? "D"}
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs font-semibold text-[#101828] truncate">
                      {currentApp?.name ?? "Digibikes"}
                    </span>
                    <span className="block text-[11px] text-[#667085] truncate font-mono">
                      {currentApp?.slug ?? scopeApp}
                    </span>
                  </div>
                </div>
                <ChevronDown className="size-4 text-[#98A2B3] shrink-0 ml-1" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 p-1.5 bg-white border border-[#EAECF0] shadow-md rounded-xl z-50">
                <DropdownMenuLabel className="text-[11px] font-semibold text-[#98A2B3] uppercase tracking-wider px-2 py-1">
                  Pilih Aplikasi
                </DropdownMenuLabel>
                {apps.map((app) => (
                  <DropdownMenuItem
                    key={app.id}
                    className={cn(
                      "flex items-center gap-2 px-2.5 py-2 text-xs rounded-lg cursor-pointer transition-colors",
                      scopeApp === app.slug ? "bg-[#EFF4FF] text-[#2563EB] font-semibold" : "text-[#344054] hover:bg-[#F6F7F9]"
                    )}
                    onClick={() => handleSelectApp(app.slug)}
                    onSelect={() => handleSelectApp(app.slug)}
                  >
                    <div className="size-5 rounded bg-[#EFF4FF] flex items-center justify-center text-[10px] font-bold text-[#2563EB]">
                      {app.name.charAt(0)}
                    </div>
                    <span>{app.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex justify-center pb-2">
            <div
              className="size-8 rounded-lg bg-[#EFF4FF] border border-[#BFDBFE] flex items-center justify-center text-[#2563EB] font-bold text-xs"
              title={currentApp?.name ?? "Digibikes"}
            >
              {currentApp?.name?.charAt(0) ?? "D"}
            </div>
          </div>
        )}

        {/* Navigation Rail */}
        <nav className="flex flex-col gap-4 overflow-y-auto px-3 py-1">
          {visibleGroups.map((group, groupIdx) => (
            <div key={groupIdx} className="flex flex-col gap-0.5">
              {group.title && !collapsed && (
                <div className="px-3 pt-3 pb-1 text-[11px] font-semibold tracking-wider text-[#98A2B3]">
                  {group.title}
                </div>
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={
                      scopeApp
                        ? `${item.to}${item.to.includes("?") ? "&" : "?"}app=${encodeURIComponent(
                            scopeApp
                          )}${scopeChannel ? `&channel=${encodeURIComponent(scopeChannel)}` : ""}`
                        : item.to
                    }
                    end={item.to === "/"}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors",
                        isActive
                          ? "bg-[#0F1B3D] text-white shadow-xs"
                          : "text-[#344054] hover:bg-[#F6F7F9] hover:text-[#101828]",
                        collapsed && "justify-center px-0"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          className={cn(
                            "size-4.5 shrink-0 transition-colors",
                            isActive ? "text-white" : "text-[#667085] group-hover:text-[#101828]"
                          )}
                        />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {!collapsed && item.badge && (
                          <span className="ml-auto rounded-full bg-[#EFF4FF] px-2 py-0.5 text-[10.5px] font-semibold text-[#2563EB]">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </div>

      {/* Sidebar Footer */}
      <div className="flex flex-col gap-1 border-t border-[#EAECF0] p-3">
        {/* Release Quick Action */}
        {canRelease && !collapsed && (
          <Button
            onClick={() => setPublishOpen(true)}
            className="w-full justify-center gap-1.5 bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs font-semibold h-8.5 rounded-lg mb-1 shadow-2xs"
          >
            <Plus className="size-3.5" />
            + Buat Rilis
          </Button>
        )}

        {/* Collapse Sidebar Button */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? "Bentangkan menu" : "Ciutkan menu"}
          className={cn(
            "hidden lg:flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[12.5px] text-[#475467] hover:bg-[#F6F7F9] hover:text-[#101828] transition-colors",
            collapsed && "justify-center px-0"
          )}
        >
          <ChevronLeft
            className={cn(
              "size-4 shrink-0 text-[#667085] transition-transform duration-200",
              collapsed && "rotate-180"
            )}
          />
          {!collapsed && <span>Ciutkan bilah samping</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F6F7F9]">
      {/* Desktop Sidebar Rail */}
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 border-r border-[#EAECF0] bg-white transition-[width] duration-200 ease-in-out lg:flex lg:flex-col",
          collapsed ? "w-[72px]" : "w-[240px]"
        )}
      >
        {sidebarContent}
      </aside>

      {/* Main Container */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top Navbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#EAECF0] bg-white px-4 sm:px-6 shadow-2xs">
          {/* Left: Mobile trigger & Breadcrumb */}
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={mobileNav} onOpenChange={setMobileNav}>
              <SheetTrigger
                render={
                  <Button variant="ghost" size="icon" className="lg:hidden text-[#475467]" aria-label="Buka menu">
                    <Menu className="size-5" />
                  </Button>
                }
              />
              <SheetContent side="left" className="w-[280px] p-0 bg-white">
                <SheetTitle className="sr-only">Navigasi Konsol OTA</SheetTitle>
                {sidebarContent}
              </SheetContent>
            </Sheet>

            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[13px]">
              <span className="text-[#667085] hidden sm:inline">{breadcrumb.group}</span>
              <span className="text-[#D0D5DD] hidden sm:inline">/</span>
              <span className="font-semibold text-[#101828] truncate">{breadcrumb.page}</span>
            </nav>
          </div>

          {/* Right: Search, Scope Pill Dropdown, Notification, User Avatar */}
          <div className="flex items-center gap-3">
            {/* Search Bar */}
            <form onSubmit={submitSearch} className="hidden md:block">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#98A2B3]" />
                <Input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Cari atau lompat ke..."
                  className="h-8.5 w-[200px] lg:w-[260px] pl-9 pr-10 text-[12.5px] bg-[#F6F7F9] border-[#EAECF0] rounded-lg focus-visible:bg-white focus-visible:ring-1 focus-visible:ring-[#2563EB]"
                />
                <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium text-[#667085] bg-white border border-[#EAECF0] px-1.5 py-0.5 rounded shadow-2xs">
                  ⌘K
                </kbd>
              </div>
            </form>

            {/* Scope Environment Pill Dropdown - Clickable to switch channels */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1.5 rounded-full border border-[#D0D5DD] bg-white px-3 py-1 text-[12px] font-medium text-[#344054] shadow-2xs hover:border-[#98A2B3] hover:bg-[#F6F7F9] transition-colors focus:outline-none cursor-pointer"
                aria-label="Pilih kanal aktif"
              >
                <span
                  className={cn(
                    "size-2 rounded-full",
                    scopeChannel === "staging"
                      ? "bg-[#F79009]"
                      : scopeChannel === "local"
                      ? "bg-[#854D0E]"
                      : "bg-[#17B26A]"
                  )}
                />
                <span className="capitalize">{scopeChannel || "Semua Kanal"}</span>
                <ChevronDown className="size-3.5 text-[#98A2B3] ml-0.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 p-1.5 bg-white border border-[#EAECF0] shadow-md rounded-xl z-50">
                <DropdownMenuLabel className="text-[11px] font-semibold text-[#98A2B3] uppercase tracking-wider px-2 py-1">
                  Kanal Rollout ({currentApp?.name ?? "Aplikasi"})
                </DropdownMenuLabel>
                <DropdownMenuItem
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md cursor-pointer",
                    !scopeChannel ? "bg-[#EFF4FF] text-[#2563EB] font-semibold" : "text-[#344054] hover:bg-[#F6F7F9]"
                  )}
                  onClick={() => handleSelectChannel("")}
                  onSelect={() => handleSelectChannel("")}
                >
                  <span className="size-1.5 rounded-full bg-[#667085]" />
                  <span>Semua Kanal</span>
                </DropdownMenuItem>
                {channels.map((ch) => (
                  <DropdownMenuItem
                    key={ch.id}
                    className={cn(
                      "flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md cursor-pointer",
                      scopeChannel === ch.name ? "bg-[#EFF4FF] text-[#2563EB] font-semibold" : "text-[#344054] hover:bg-[#F6F7F9]"
                    )}
                    onClick={() => handleSelectChannel(ch.name)}
                    onSelect={() => handleSelectChannel(ch.name)}
                  >
                    <span
                      className={cn(
                        "size-1.5 rounded-full",
                        ch.name === "staging" ? "bg-[#F79009]" : ch.name === "local" ? "bg-[#854D0E]" : "bg-[#17B26A]"
                      )}
                    />
                    <span className="capitalize">{ch.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-2 rounded-full focus:outline-none cursor-pointer"
                aria-label="Menu akun"
              >
                <Avatar className="size-8.5 border border-[#EAECF0] shadow-2xs">
                  <AvatarFallback className="bg-[#0F1B3D] text-[12px] font-semibold text-white">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-1.5 bg-white border border-[#EAECF0] shadow-md rounded-xl z-50">
                <DropdownMenuLabel className="flex flex-col px-2 py-1.5">
                  <span className="font-semibold text-sm text-[#101828]">{user?.username ?? "Administrator"}</span>
                  <span className="text-[12px] font-normal text-[#667085]">
                    {user ? ROLE_LABEL[user.role] : "Operator OTA"}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-1 bg-[#EAECF0]" />
                <DropdownMenuItem
                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#344054] rounded-md cursor-pointer hover:bg-[#F6F7F9]"
                  onClick={() => navigate("/change-password")}
                  onSelect={() => navigate("/change-password")}
                >
                  <Settings2 className="size-4 text-[#667085]" />
                  Ubah kata sandi
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1 bg-[#EAECF0]" />
                <DropdownMenuItem
                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#F04438] rounded-md cursor-pointer hover:bg-[#FEF3F2]"
                  onClick={() => void signOut()}
                  onSelect={() => void signOut()}
                >
                  <LogOut className="size-4" />
                  Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content Body */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {publishOpen && (
        <PublishDialog
          apps={apps}
          defaultChannel={scopeChannel || "production"}
          onClose={() => setPublishOpen(false)}
        />
      )}

      {/* Mounted here, not on the builds page: a transfer must keep reporting
          while the operator works somewhere else. */}
      <UploadDock />
    </div>
  );
}
